import React, { useCallback, useEffect, useState } from "react";
import { connect } from "react-redux";

import { faTimes } from "@fortawesome/pro-solid-svg-icons/faTimes";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Popconfirm from "antd/es/popconfirm";
import cn from "classnames";
import { isEqual } from "lodash-es";
import { defineMessage, useIntl } from "react-intl";

import Address from "@mapmycustomers/shared/types/Address";
import Located, { GeoManagementState } from "@mapmycustomers/shared/types/base/Located";
import LongLat from "@mapmycustomers/shared/types/base/LongLat";
import { EntityWithLocation } from "@mapmycustomers/shared/types/entity";
import { stopEvents } from "@mapmycustomers/shared/util/browser";
import geoService, {
  convertLatLngToLongLat,
  convertLongLatToGeoPoint,
} from "@mapmycustomers/shared/util/geo/GeoService";
import { LoadingSpinner, Modal } from "@mapmycustomers/ui";

import RecordLocationMiniMap from "@app/component/field/LocationMiniMap/RecordLocationMiniMap";
import { reverseGeocodeAddress } from "@app/store/location/actions";
import { formatCoordinates, getFormattedAddressForUi } from "@app/util/formatters";
import geoAddressToAddress from "@app/util/geoAddressToAddress";

import styles from "./ChangePinLocationModal.module.scss";

interface Props {
  entity: EntityWithLocation;
  onHide: () => void;
  onReverseGeocodeAddress: typeof reverseGeocodeAddress;
  onUpdate: (located: Located) => void;
}

const geocodingFailedMessage = defineMessage({
  id: "recordPane.mapPopup.addressUndefined",
  defaultMessage: "Cannot determine address at this location",
  description: "Edit pane / details / Cannot determine address at this location",
});

const ChangePinLocationModal: React.FC<Props> = ({
  entity,
  onHide,
  onReverseGeocodeAddress,
  onUpdate,
}) => {
  const intl = useIntl();

  const [isMapReady, setMapReady] = useState<boolean>(false);
  const [centerPoint, setCenterPoint] = useState<LongLat>();
  const [initialZoom, setInitialZoom] = useState<number>(16);
  const [infoString, setInfoString] = useState<string>(getFormattedAddressForUi(entity as Address));
  const [coordinatesString, setCoordinatesString] = useState<string>(
    formatCoordinates(entity.geoPoint?.coordinates)
  );
  const [addressChanged, setAddressChanged] = useState(false);
  const [coordinatesChanged, setCoordinatesChanged] = useState(false);
  const hasChanges = addressChanged || coordinatesChanged;
  const [hasError, setHasError] = useState(false);
  const [located, setLocated] = useState<Located | undefined>();

  const handleGeocode = useCallback(
    (latLng: google.maps.LatLng) => {
      const coordinates = convertLatLngToLongLat(latLng);
      onReverseGeocodeAddress({
        callback: (result) => {
          setCoordinatesChanged(true);
          if (result.status === google.maps.GeocoderStatus.OK) {
            const located: Located = {
              ...result.address,
              geoAddress: result.address,
              geoCodedAt: null,
              geoCodeType: null,
              geoManagementState: GeoManagementState.MANUAL,
              geoPoint: convertLongLatToGeoPoint(coordinates),
              geoSource: null,
              geoStatus: null,
            };
            const updatedAddress = geoAddressToAddress(result.address);
            const previousAddress = geoAddressToAddress(entity.geoAddress ?? {});
            if (!isEqual(updatedAddress, previousAddress)) {
              setAddressChanged(true);
            }
            setHasError(false);
            setInfoString(getFormattedAddressForUi(updatedAddress));
            setCoordinatesString(formatCoordinates(result.geoPoint?.coordinates));
            setLocated(located);
          } else {
            setHasError(true);
            setCoordinatesString(formatCoordinates(convertLatLngToLongLat(latLng)));
          }
        },
        coordinates,
      });
    },
    [entity.geoAddress, onReverseGeocodeAddress]
  );

  useEffect(() => {
    // TODO: do we really need this?
    Promise.race([
      new Promise<LongLat>((resolve) => {
        if (entity?.geoPoint?.coordinates) {
          resolve(entity.geoPoint.coordinates);
        } else {
          return geoService
            .getCurrentPosition()
            .then((pos) => {
              resolve([pos.coords.longitude, pos.coords.latitude]);
            })
            .catch(() => {
              resolve([0, 0]);
              setInitialZoom(2);
            });
        }
      })
        .catch(() => {
          setInitialZoom(2);
          return [0, 0] as LongLat;
        })
        .then(([lng, lat]) => setCenterPoint([lng, lat])),

      new Promise((resolve) => setTimeout(resolve, 7000)),
    ]).finally(() => setMapReady(true));
  }, [entity, setCenterPoint, setMapReady]);

  const handleOkClick = useCallback(() => {
    if (located) {
      onHide();
      onUpdate(located);
    }
  }, [located, onHide, onUpdate]);

  return (
    <Modal
      cancelText={intl.formatMessage({
        id: "recordPane.mapPopup.cancel",
        defaultMessage: "Cancel",
        description: "Label for cancel button inside change pin modal in record pane view",
      })}
      closeIcon={
        <p onClick={hasChanges ? stopEvents : undefined}>
          <Popconfirm
            cancelText="Cancel"
            disabled={!hasChanges || hasError}
            okText="Discard"
            onConfirm={onHide}
            placement="rightTop"
            title={intl.formatMessage(
              {
                id: "recordPane.mapPopup.close.popconfirm.title",
                defaultMessage: "Are you sure you want to {br}exit without saving?",
                description: "Confirmation text when closing Change Pin location modal",
              },
              {
                br: <br />,
              }
            )}
          >
            <FontAwesomeIcon icon={faTimes} />
          </Popconfirm>
        </p>
      }
      maskClosable={false}
      okButtonProps={{
        disabled: !hasChanges || !located || hasError,
      }}
      okText={intl.formatMessage({
        id: "recordPane.mapPopup.update",
        defaultMessage: "Update address",
        description: "Label for close update inside change pin modal in record pane view",
      })}
      onCancel={onHide}
      onOk={handleOkClick}
      scrollable
      title={intl.formatMessage({
        id: "recordPane.mapPopup.mapTitle",
        defaultMessage: "Map Preview",
        description: "Edit pane / details / map modal title",
      })}
      visible
      width="60vw"
    >
      {isMapReady && centerPoint ? (
        <RecordLocationMiniMap
          className={styles.map}
          draggable
          entity={{
            ...entity,
            // we override geoPoint because it might also be a current user position in case
            // record has no position currently
            geoPoint: { coordinates: centerPoint, type: "Point" },
          }}
          mapOptions={{
            center: {
              lat: centerPoint[1],
              lng: centerPoint[0],
            },
            zoom: initialZoom,
          }}
          onDragPin={handleGeocode}
        />
      ) : (
        <LoadingSpinner />
      )}

      <div className={styles.info}>
        <div className={styles.infoHelp}>
          {intl.formatMessage({
            id: "recordPane.mapPopup.noLocationDetected",
            defaultMessage: "Drag and drop pin to adjust location",
            description: "Edit pane / details / no location detected error message",
          })}
        </div>
        {hasError ? (
          <div className={styles.error}>{intl.formatMessage(geocodingFailedMessage)}</div>
        ) : (
          <div className={cn({ [styles.highlight]: addressChanged })}>{infoString}</div>
        )}
        {coordinatesString ? (
          <div className={cn({ [styles.highlight]: coordinatesChanged })}>{coordinatesString}</div>
        ) : null}
      </div>
    </Modal>
  );
};

const mapDispatchToProps = {
  onReverseGeocodeAddress: reverseGeocodeAddress,
};

export default connect(null, mapDispatchToProps)(ChangePinLocationModal);
